import React, { useState, useEffect, useReducer } from 'react'
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    Avatar,
    AppBar,
    Toolbar,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Chip,
} from '@mui/material'
import {
    BarChart,
    Navigation,
    Circle,
    TrendingUp,
    DirectionsBoat,
    Engineering,
    Warning,
} from '@mui/icons-material'
import {
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
} from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { DTCEngineModelChart } from '../charts/DTCEngineModelChart'
import { DTCSeverityChart } from '../charts/DTCSeverityChart'
import { DTCBoatModelChart } from '../charts/DTCBoatModelChart'
import { DTCEngineHoursChart } from '../charts/DTCEngineHoursChart'
import { DTCBoatModelYearChart } from '../charts/DTCBoatModelYearChart'
import { DTCTimelineChart } from '../charts/DTCTimelineChart'
import { Grid } from '../../../UniversalComponents'
import { getBoatDTCs } from '../../../../apiRoutes'
import { useAuth } from '../../../../context/AuthenticationContext'
import { useSocket } from '../../../../context/SocketContext'

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#ff6b35',
        },
        secondary: {
            main: '#4caf50',
        },
        background: {
            default: '#1a1a1a',
            paper: '#2d2d2d',
        },
        text: {
            primary: '#ffffff',
            secondary: '#b0b0b0',
        },
    },
})

interface DTCEvent {
    date: string
    codeDescription: string
    eventCount: number
    severity: 'yellow' | 'green' | 'red'
}

interface DTCDescription {
    codeDescription: string
    spnFmi: string
    eventCount: number
}

interface FilterOption {
    label: string
    checked: boolean
}

interface RealDTCEvent {
    dtc_timestamp: string
    DTCNumber: number
    SPN: number
    FMI: number
    SpnConvMode: number
    OccurCounter: number
    MalfunctionIndication: number
    RedStop: number
    AmberWarning: number
    Protect: number
    source: number
}

interface FleetData {
    id: string
    device?: string
    fullHin?: string
    model?: string
    modelYear?: number
    manufacturer?: string
}

export const ExecutiveDashboard: React.FC = () => {
    const { user } = useAuth().auth
    const { apiSocket } = useSocket()
    const activeManufacturer = user?.activeManufacturer?._id

    const defaultState = {
        fleetData: [] as FleetData[],
        realDTCEvents: [] as RealDTCEvent[],
        selectedDevice: '',
        loading: false,
    }
    const reducer = (state: any, newState: any) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const { fleetData, realDTCEvents, selectedDevice, loading } = state

    // This needs to be gathered from mongoDB
    const [hinOptions, setHinOptions] = useState<FilterOption[]>([
        { label: '00001', checked: false },
        { label: '00002', checked: false },
        { label: '00003', checked: false },
        { label: '00004', checked: false },
    ])

    // This needs to be gathered from mongoDB
    const [modelYearOptions, setModelYearOptions] = useState<FilterOption[]>([
        { label: '(Blank)', checked: false },
        { label: '2020', checked: false },
        { label: '2021', checked: false },
        { label: '2022', checked: false },
        { label: '2023', checked: false },
        { label: '2024', checked: false },
        { label: '2025', checked: false },
    ])

    // This needs to be gathered from mongoDB
    const [boatModelOptions, setBoatModelOptions] = useState<FilterOption[]>([
        { label: '(Blank)', checked: false },
        { label: '200', checked: false },
        { label: '210', checked: false },
        { label: '230', checked: false },
        { label: 'G21', checked: false },
        { label: 'G23', checked: false },
        { label: 'G25', checked: false },
    ])

    // This needs to be gathered from mongoDB
    const [engineModelOptions, setEngineModelOptions] = useState<
        FilterOption[]
    >([
        { label: '(Blank)', checked: false },
        { label: 'INGENITY ELECTRIC D...', checked: false },
        { label: 'NA', checked: false },
        { label: 'UNSPECIFIED ENGINE', checked: false },
        { label: 'XR7 6.2L Supercharged...', checked: false },
        { label: 'XS7 Coastal 550HP -...', checked: false },
        { label: 'Yanmar 8LV Diesel 4.5...', checked: false },
        { label: 'Yanmar 8LV Diesel Co...', checked: false },
        { label: 'ZR4 6.0L', checked: false },
        { label: 'ZR4 Coastal 6.0L', checked: false },
    ])

    const [statusOptions, setStatusOptions] = useState<FilterOption[]>([
        { label: 'After Ship', checked: false },
        { label: 'Before Ship', checked: false },
    ])

    // Fetch fleet data
    useEffect(() => {
        if (!apiSocket) return
        apiSocket.emit('allFleetLiveData', { user })

        const handler = ({ data }: { data: any[] }) => {
            if (!data) return

            const fleetRows = data
                .filter((t) => t?.boatData?.manufacturer?._id === activeManufacturer)
                .map((t) => ({
                    id: t?.boatData?._id || Math.random().toString(),
                    device: t?.boatData?.device?.deviceSerial || '',
                    fullHin: t?.boatData?.fullHin || '',
                    model: t?.boatData?.boatModel?.model || '',
                    modelYear: t?.boatData?.boatModel?.modelYear || 0,
                    manufacturer: t?.boatData?.manufacturer?.manufacturerName || '',
                }))
                .filter(row => row.device) // Only include boats with devices

            setState({ fleetData: fleetRows })
            
            // Auto-select first device if none selected
            if (fleetRows.length > 0 && !selectedDevice) {
                setState({ selectedDevice: fleetRows[0].device })
            }
        }

        apiSocket.on('allFleetLiveData', handler)
        return () => {
            apiSocket.off('allFleetLiveData', handler)
        }
    }, [apiSocket, user, activeManufacturer, selectedDevice])

    // Fetch DTC data when device is selected
    useEffect(() => {
        if (!selectedDevice) return

        const fetchDTCData = async () => {
            setState({ loading: true })
            try {
                const dtcData = await getBoatDTCs({ 
                    deviceSerial: selectedDevice, 
                    limit: 100 
                })
                setState({ realDTCEvents: dtcData || [] })
            } catch (error) {
                console.error('Error fetching DTC data:', error)
                setState({ realDTCEvents: [] })
            } finally {
                setState({ loading: false })
            }
        }

        fetchDTCData()
    }, [selectedDevice])

    const handleFilterChange = (
        options: FilterOption[],
        setOptions: React.Dispatch<React.SetStateAction<FilterOption[]>>,
        index: number
    ) => {
        setOptions((prev) =>
            prev.map((option, i) =>
                i === index ? { ...option, checked: !option.checked } : option
            )
        )
    }

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'red':
                return '#f44336'
            case 'yellow':
                return '#ff9800'
            case 'green':
                return '#4caf50'
            default:
                return '#9e9e9e'
        }
    }

    const getDTCSeverity = (dtc: RealDTCEvent) => {
        if (dtc.RedStop === 1) return 'red'
        if (dtc.AmberWarning === 1) return 'yellow'
        if (dtc.MalfunctionIndication === 1) return 'green'
        return 'gray'
    }

    const getDTCDescription = (spn: number, fmi: number) => {
        // This would typically come from a lookup table or API
        // For now, return a generic description
        return `SPN ${spn} - FMI ${fmi}`
    }

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                bgcolor: 'background.default',
            }}
        >
            {/* Header */}
            <AppBar position="static" sx={{ bgcolor: 'background.paper' }}>
                <Toolbar>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <BarChart />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            variant="h6"
                            component="div"
                            color="primary"
                            fontWeight="bold"
                        >
                            OSMOSIS
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Executive DTC Analytics Dashboard
                        </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                            sx={{
                                bgcolor: '#4caf50',
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="white"
                                fontWeight="bold"
                            >
                                OSMOSIS
                            </Typography>
                        </Box>
                    </Stack>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {/* Left Sidebar - Filters */}
                    <Grid item xs={12} md={2}>
                        <Stack spacing={2}>
                            {/* Device Selector */}
                            <Paper sx={{ p: 2 }}>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    gutterBottom
                                >
                                    Select Device
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Device</InputLabel>
                                    <Select
                                        value={selectedDevice}
                                        onChange={(e) => setState({ selectedDevice: e.target.value })}
                                        label="Device"
                                    >
                                        {fleetData.map((boat) => (
                                            <MenuItem key={boat.id} value={boat.device}>
                                                {boat.fullHin || boat.device} - {boat.model} {boat.modelYear}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Paper>

                            {/* HIN Filter */}
                            <Paper sx={{ p: 2 }}>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    gutterBottom
                                >
                                    HIN #
                                </Typography>
                                <FormGroup>
                                    {hinOptions.map((option, index) => (
                                        <FormControlLabel
                                            key={option.label}
                                            control={
                                                <Checkbox
                                                    checked={option.checked}
                                                    onChange={() =>
                                                        handleFilterChange(
                                                            hinOptions,
                                                            setHinOptions,
                                                            index
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {option.label}
                                                </Typography>
                                            }
                                        />
                                    ))}
                                </FormGroup>
                            </Paper>

                            {/* Model Year Filter */}
                            <Paper sx={{ p: 2 }}>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    gutterBottom
                                >
                                    Model Year
                                </Typography>
                                <FormGroup>
                                    {modelYearOptions.map((option, index) => (
                                        <FormControlLabel
                                            key={option.label}
                                            control={
                                                <Checkbox
                                                    checked={option.checked}
                                                    onChange={() =>
                                                        handleFilterChange(
                                                            modelYearOptions,
                                                            setModelYearOptions,
                                                            index
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {option.label}
                                                </Typography>
                                            }
                                        />
                                    ))}
                                </FormGroup>
                            </Paper>

                            {/* Boat Model Filter */}
                            <Paper sx={{ p: 2 }}>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    gutterBottom
                                >
                                    Boat Model
                                </Typography>
                                <FormGroup>
                                    {boatModelOptions.map((option, index) => (
                                        <FormControlLabel
                                            key={option.label}
                                            control={
                                                <Checkbox
                                                    checked={option.checked}
                                                    onChange={() =>
                                                        handleFilterChange(
                                                            boatModelOptions,
                                                            setBoatModelOptions,
                                                            index
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {option.label}
                                                </Typography>
                                            }
                                        />
                                    ))}
                                </FormGroup>
                            </Paper>

                            {/* Engine Model Filter */}
                            <Paper sx={{ p: 2 }}>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    gutterBottom
                                >
                                    Engine Model
                                </Typography>
                                <FormGroup>
                                    {engineModelOptions
                                        .slice(0, 5)
                                        .map((option, index) => (
                                            <FormControlLabel
                                                key={option.label}
                                                control={
                                                    <Checkbox
                                                        checked={option.checked}
                                                        onChange={() =>
                                                            handleFilterChange(
                                                                engineModelOptions,
                                                                setEngineModelOptions,
                                                                index
                                                            )
                                                        }
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {option.label}
                                                    </Typography>
                                                }
                                            />
                                        ))}
                                </FormGroup>
                            </Paper>

                            {/* Status Filter */}
                            <Paper sx={{ p: 2 }}>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    gutterBottom
                                >
                                    Status at Time of Event
                                </Typography>
                                <FormGroup>
                                    {statusOptions.map((option, index) => (
                                        <FormControlLabel
                                            key={option.label}
                                            control={
                                                <Checkbox
                                                    checked={option.checked}
                                                    onChange={() =>
                                                        handleFilterChange(
                                                            statusOptions,
                                                            setStatusOptions,
                                                            index
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {option.label}
                                                </Typography>
                                            }
                                        />
                                    ))}
                                </FormGroup>
                            </Paper>

                            {/* Summary Cards */}
                            <Card sx={{ bgcolor: '#404040', color: 'white' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Warning sx={{ color: '#FFA726' }} />
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="#FFA726"
                                            >
                                                Total DTCs
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                            >
                                                {realDTCEvents.length}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={{ bgcolor: '#404040', color: 'white' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <DirectionsBoat
                                            sx={{ color: '#FFA726' }}
                                        />
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="#FFA726"
                                            >
                                                Available Devices
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                            >
                                                {fleetData.length}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>

                    {/* Main Content */}
                    <Grid item xs={12} md={10}>
                        <Grid container spacing={3}>
                            {/* Top Row Charts */}
                            <Grid item xs={12} md={4}>
                                <DTCEngineModelChart
                                    title="DTC Events by Engine Model"
                                    data={[]}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <DTCSeverityChart
                                    title="DTC Events by Severity"
                                    data={[]}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, height: 350 }}>
                                    <Typography
                                        variant="h6"
                                        color="primary"
                                        gutterBottom
                                    >
                                        DTC Events (All Columns)
                                    </Typography>
                                    {loading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <TableContainer sx={{ maxHeight: 280, overflowX: 'auto' }}>
                                            <Table size="small" sx={{ minWidth: 800 }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Timestamp</TableCell>
                                                        <TableCell>DTC Number</TableCell>
                                                        <TableCell>SPN</TableCell>
                                                        <TableCell>FMI</TableCell>
                                                        <TableCell>Spn Conv Mode</TableCell>
                                                        <TableCell>Occur Counter</TableCell>
                                                        <TableCell>Malfunction Indication</TableCell>
                                                        <TableCell>Red Stop</TableCell>
                                                        <TableCell>Amber Warning</TableCell>
                                                        <TableCell>Protect</TableCell>
                                                        <TableCell>Source</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {realDTCEvents.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={11} align="center">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No DTC events found
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        realDTCEvents.slice(0, 10).map((event, index) => (
                                                            <TableRow key={index} hover>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {new Date(event.dtc_timestamp).toLocaleString()}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {event.DTCNumber}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {event.SPN}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {event.FMI}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {event.SpnConvMode}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {event.OccurCounter}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                        <Circle
                                                                            sx={{
                                                                                fontSize: 8,
                                                                                color: event.MalfunctionIndication === 1 ? '#4caf50' : '#9e9e9e',
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2">
                                                                            {event.MalfunctionIndication}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                        <Circle
                                                                            sx={{
                                                                                fontSize: 8,
                                                                                color: event.RedStop === 1 ? '#f44336' : '#9e9e9e',
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2">
                                                                            {event.RedStop}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                        <Circle
                                                                            sx={{
                                                                                fontSize: 8,
                                                                                color: event.AmberWarning === 1 ? '#ff9800' : '#9e9e9e',
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2">
                                                                            {event.AmberWarning}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {event.Protect}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {event.source}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Paper>
                            </Grid>

                            {/* Middle Row Charts */}
                            <Grid item xs={12} md={4}>
                                <DTCBoatModelChart
                                    title="DTC Events by Boat Model"
                                    data={[]}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <DTCEngineHoursChart
                                    title="DTC Events by Engine Hours Group"
                                    data={[]}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, height: 350 }}>
                                    <Typography
                                        variant="h6"
                                        color="primary"
                                        gutterBottom
                                    >
                                        DTC Summary by SPN-FMI
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 280 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        SPN-FMI
                                                    </TableCell>
                                                    <TableCell>
                                                        DTC Number
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Occurrences
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {realDTCEvents.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={3} align="center">
                                                            <Typography variant="body2" color="text.secondary">
                                                                No DTC events found
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    realDTCEvents
                                                        .slice(0, 10)
                                                        .map((event, index) => (
                                                            <TableRow
                                                                key={index}
                                                                hover
                                                            >
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="medium"
                                                                    >
                                                                        {event.SPN}-{event.FMI}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                    >
                                                                        {event.DTCNumber}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="medium"
                                                                    >
                                                                        {event.OccurCounter}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Grid>

                            {/* Bottom Row Charts */}
                            <Grid item xs={12} md={4}>
                                <DTCBoatModelYearChart
                                    title="DTC Events by Boat Model Year"
                                    data={[]}
                                />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <DTCTimelineChart title="Number of DTCs Per Day" />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}
